import React from 'react';
import { format, addDays, isBefore, parseISO, isValid } from 'date-fns';
import { es } from 'date-fns/locale';
import { IconPlus } from './icons';

export const __toYMD = (value) => {
  if (!value) return null;
  if (typeof value === 'string') {
    const m = value.match(/^(\d{4}-\d{2}-\d{2})/);
    if (m) return m[1];
  }
  const d = new Date(value);
  if (isNaN(d)) return null;
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};

export const monthLabel = (d) => format(d, "MMMM yyyy", { locale: es });

export const getDerivedStatus = (p) => {
  if (p.status === 'pagado') return 'pagado';
  const due = p.due_date ? parseISO(p.due_date) : null;
  if (!due || !isValid(due)) return 'sin_fecha';
  const today = new Date(); today.setHours(0,0,0,0);
  const soon = addDays(today, 3);
  if (isBefore(due, today)) return 'atrasado';
  if (isBefore(due, soon)) return 'por_vencer';
  return 'pendiente';
};

export const STATUS_OPTIONS = [
  { value: 'atrasado',   label: 'Atrasado' },
  { value: 'por_vencer', label: 'Por vencer' },
  { value: 'pendiente',  label: 'Pendiente' },
  { value: 'pagado',     label: 'Pagado' },
];

export const StatusPill = ({ status, due_date }) => {
  if (!due_date && status !== 'pagado') {
    return <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-slate-100 text-slate-700">Sin fecha</span>;
  }
  const d = due_date ? parseISO(due_date) : null;
  const today = new Date(); today.setHours(0,0,0,0);
  const soon = addDays(today, 3);

  if (status === 'pagado') {
    return <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200/50">Pagado</span>;
  }
  if (!d || isBefore(d, today)) {
    return <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-rose-100 text-rose-700 ring-1 ring-rose-200/60">Atrasado</span>;
  }
  if (isBefore(d, soon)) {
    return <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-amber-100 text-amber-800 ring-1 ring-amber-200/60">Por vencer</span>;
  }
  return <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-indigo-100 text-indigo-700 ring-1 ring-indigo-200/60">Pendiente</span>;
};

export const MoneyCell = ({ amount, currency }) => (
  <span className="font-semibold tracking-tight text-slate-800">
    {new Intl.NumberFormat('es-MX', { style: 'currency', currency: currency || 'MXN' })
      .format(Number(amount) || 0)}
  </span>
);

export const SkeletonRow = ({ cols = 7 }) => (
  <tr className="animate-pulse">
    {Array.from({ length: cols }).map((_, i) => (
      <td key={i} className="px-6 py-5">
        <div className="h-3 w-32 rounded bg-slate-200/70" />
      </td>
    ))}
  </tr>
);

export const EmptyState = ({ onNew, userRole }) => {
  const roleLc = (userRole ?? '').toString().toLowerCase();
  const isAdmin = roleLc === 'admin';
  const isContador = roleLc === 'contador';
  return (
    <div className="flex flex-col items-center justify-center py-16">
      <svg className="h-24 w-24 text-slate-300 mb-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <rect x="3" y="5" width="18" height="14" rx="3" className="stroke-current/40"/>
        <path d="M3 10h18" className="stroke-current/40"/>
        <path d="M8 3v4M16 3v4" className="stroke-current/40"/>
      </svg>
      <h3 className="text-lg font-semibold text-slate-700">Sin pagos por ahora</h3>
      <p className="text-slate-500 mt-1">
        {isContador 
          ? 'No hay pagos marcados como "Pagado" en este rango.'
          : 'Crea tu primer registro para empezar a organizar tus finanzas.'
        }
      </p>
      {isAdmin && (
        <button onClick={onNew} className="mt-6 inline-flex items-center gap-2 rounded-xl bg-slate-900 text-white px-4 py-2 shadow-lg hover:shadow-xl transition">
          <IconPlus className="h-4 w-4" /> Nuevo pago
        </button>
      )}
    </div>
  );
};
